import { ChakraV3, Spin } from '@piccolohealth/ui';
import { z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Navigate } from 'react-router-dom';
import { HookedFormField } from '../../../components/forms/HookedFormField';
import { HookedPhoneNumberInput } from '../../../components/forms/HookedPhoneNumberInput';
import { HookedSubmitButton } from '../../../components/forms/V3HookedSubmitButton';
import { useAuth } from '../../../context/AuthContext';
import { sendExceptionToSentry } from '../../../utils/errors';
import { zodResolver } from '../../../utils/zod';
import { AuthCard } from './AuthCard';
import { AuthLayout } from './AuthLayout';
import { MFAChooseMethodButton } from './MFAChooseMethodButton';

type FormValues = {
  phoneNumber: string;
};

const schema: z.ZodSchema<FormValues> = z.object({
  phoneNumber: z.string().min(1, 'Phone number is required'),
});

interface Props {
  onSendCode: (phoneNumber: string) => Promise<void>;
}

export const PhoneEnrolCard = (props: Props) => {
  const { onSendCode } = props;
  const auth = useAuth();

  const methods = useForm<FormValues>({
    defaultValues: {
      phoneNumber: '+61 ',
    },
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormValues) => {
    const phoneNumber = data.phoneNumber.replaceAll(' ', '');

    try {
      await onSendCode(phoneNumber);
    } catch (err) {
      switch (err.code) {
        case 'auth/captcha-check-failed':
          methods.setError('phoneNumber', {
            message: 'reCAPTCHA verification failed. Please reload and try again',
          });
          return;
        case 'auth/invalid-phone-number':
          methods.setError('phoneNumber', {
            message: 'Invalid phone number',
          });
          return;
        case 'auth/unverified-email':
          methods.setError('phoneNumber', {
            message: 'Please verify your email before enrolling in MFA',
          });
          return;
        default:
          methods.setError('phoneNumber', {
            message: 'Service temporarily unavailable. Please contact support',
          });
          return sendExceptionToSentry(err);
      }
    }
  };

  if (auth.isLoading) {
    return (
      <AuthLayout>
        <Spin />
      </AuthLayout>
    );
  }

  if (!auth.showEnrolMfa) {
    return <Navigate to='/login' />;
  }

  return (
    <AuthLayout>
      <AuthCard>
        <ChakraV3.Stack gap={0}>
          <ChakraV3.Text fontWeight='semibold' fontSize='2xl'>
            Secure Your Account
          </ChakraV3.Text>
          <ChakraV3.Text fontSize='sm' color='secondary'>
            Enter your phone number below. An SMS will be sent to that number with a code to enter
            on the next screen.
          </ChakraV3.Text>
        </ChakraV3.Stack>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Stack gap={8}>
              <HookedFormField name='phoneNumber'>
                <HookedPhoneNumberInput name='phoneNumber' size='lg' />
              </HookedFormField>
              <HookedSubmitButton size='md' disabled={false} loadingText='Sending code...'>
                Send code
              </HookedSubmitButton>
            </ChakraV3.Stack>
          </form>
        </FormProvider>
        <MFAChooseMethodButton />
      </AuthCard>
    </AuthLayout>
  );
};
